'use client';

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { FiBox, FiFileText, FiUser, FiHash } from 'react-icons/fi';
import styles from './SearchBar.module.css';

function detectSearchType(q: string): { type: string; path: string } | null {
  const trimmed = q.trim();
  if (/^0x[a-fA-F0-9]{64}$/.test(trimmed)) {
    return { type: 'tx', path: `/tx/${trimmed}` };
  }
  if (/^0x[a-fA-F0-9]{40}$/.test(trimmed)) {
    return { type: 'address', path: `/address/${trimmed}` };
  }
  if (/^\d+$/.test(trimmed) && Number(trimmed) > 0) {
    return { type: 'block', path: `/block/${trimmed}` };
  }
  return null;
}

function getItemLink(item: any): string {
  switch (item.type) {
    case 'block': return `/block/${item.block_number || item.block_hash}`;
    case 'address': return `/address/${item.address}`;
    case 'contract': return `/address/${item.address}`;
    case 'transaction': return `/tx/${item.tx_hash}`;
    case 'token': return `/token/${item.address}`;
    case 'label': return `/address/${item.address}`;
    default: return item.url || '#';
  }
}

function getItemIcon(type: string) {
  switch (type) {
    case 'block': return <FiBox size={16} />;
    case 'transaction': return <FiFileText size={16} />;
    case 'address': return <FiUser size={16} />;
    case 'contract': return <FiFileText size={16} />;
    case 'token': return <FiHash size={16} />;
    default: return <FiHash size={16} />;
  }
}

function getItemTitle(item: any, query: string): React.ReactNode {
  let title = '';
  switch (item.type) {
    case 'block': title = String(item.block_number); break;
    case 'transaction': title = item.tx_hash || ''; break;
    case 'address': title = item.name || item.address || ''; break;
    case 'contract': title = item.name || item.address || ''; break;
    case 'token': title = `${item.name || ''} ${item.symbol ? `(${item.symbol})` : ''}`; break;
    case 'label': title = item.name || item.address || ''; break;
    default: title = item.name || item.address || '';
  }
  // Highlight matching part
  if (query && title) {
    const idx = title.toLowerCase().indexOf(query.toLowerCase());
    if (idx >= 0) {
      return (
        <>
          {title.slice(0, idx)}
          <mark>{title.slice(idx, idx + query.length)}</mark>
          {title.slice(idx + query.length)}
        </>
      );
    }
  }
  return title;
}

function getItemHash(item: any): string | null {
  switch (item.type) {
    case 'block': return item.block_hash ? `${item.block_hash.slice(0, 6)}...${item.block_hash.slice(-2)}` : null;
    case 'transaction': return null;
    case 'token': return item.address ? `${item.address.slice(0, 8)}...${item.address.slice(-4)}` : null;
    case 'contract': return item.address ? `${item.address.slice(0, 8)}...${item.address.slice(-4)}` : null;
    default: return null;
  }
}

function formatTime(ts: string): string {
  if (!ts) return '';
  const d = new Date(ts);
  const pad = (n: number) => String(n).padStart(2, '0');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const offset = -d.getTimezoneOffset();
  const sign = offset >= 0 ? '+' : '-';
  const oh = String(Math.floor(Math.abs(offset) / 60)).padStart(2, '0');
  const om = String(Math.abs(offset) % 60).padStart(2, '0');
  return `${months[d.getMonth()]} ${pad(d.getDate())} ${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())} PM (${sign}${oh}:${om} UTC)`;
}

const SearchBarDesktop: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const formRef = useRef<HTMLFormElement>(null);
  const router = useRouter();

  const fetchResults = useCallback(async(q: string) => {
    if (!q || q.length < 1) {
      setResults([]);
      setShowDropdown(false);
      return;
    }
    setIsLoading(true);
    setShowDropdown(true);
    try {
      const res = await fetch(`https://ogpuscan.io/api/v2/search/quick?q=${encodeURIComponent(q)}`);
      if (res.ok) {
        const data = await res.json();
        setResults(Array.isArray(data) ? data : []);
      } else {
        setResults([]);
      }
    } catch {
      setResults([]);
    }
    setIsLoading(false);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchTerm(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchResults(val.trim()), 300);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const q = searchTerm.trim();
    if (!q) return;

    setShowDropdown(false);
    const detected = detectSearchType(q);
    if (detected) {
      router.push(detected.path);
    } else {
      router.push(`/search-results?q=${encodeURIComponent(q)}`);
    }
    setSearchTerm('');
    setResults([]);
  };

  const handleItemClick = (item: any) => {
    setShowDropdown(false);
    setSearchTerm('');
    setResults([]);
    router.push(getItemLink(item));
  };

  const handleClear = () => {
    setSearchTerm('');
    setResults([]);
    setShowDropdown(false);
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (formRef.current && !formRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Group results by type
  const grouped = results.reduce<Record<string, any[]>>((acc, item) => {
    const key = item.type || 'other';
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});

  const categoryLabels: Record<string, string> = {
    block: 'Blocks',
    transaction: 'Transactions',
    address: 'Addresses',
    contract: 'Contracts',
    token: 'Tokens',
    label: 'Labels',
  };

  return (
    <form className={styles.searchFormDesktop} onSubmit={handleSubmit} ref={formRef}>
      <div className={styles.searchInputWrapper}>
        <svg
          className={styles.searchIcon}
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M9 17A8 8 0 1 0 9 1a8 8 0 0 0 0 16zM18 18l-4.35-4.35"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <input
          type="text"
          className={styles.searchInput}
          placeholder="Search by address / txn hash / block / token..."
          value={searchTerm}
          onChange={handleChange}
          onFocus={() => { if (results.length > 0) setShowDropdown(true); }}
        />
        {searchTerm && (
          <button
            type="button"
            className={styles.clearButton}
            onClick={handleClear}
          >
            ×
          </button>
        )}
      </div>

      {showDropdown && (
        <div className={styles.dropdown}>
          {isLoading && (
            <div className={styles.dropdownLoading}>Searching...</div>
          )}
          {!isLoading && results.length === 0 && searchTerm.trim().length >= 1 && (
            <div className={styles.dropdownEmpty}>
              No results found for &quot;{searchTerm.trim()}&quot;
            </div>
          )}
          {!isLoading && Object.entries(grouped).map(([type, items]) => (
            <div key={type}>
              <div className={styles.dropdownCategory}>
                {categoryLabels[type] || type}
              </div>
              {items.map((item: any, i: number) => (
                <div
                  key={`${type}-${i}`}
                  className={styles.dropdownItem}
                  onClick={() => handleItemClick(item)}
                >
                  <div className={styles.dropdownIcon}>
                    {getItemIcon(type)}
                  </div>
                  <div className={styles.dropdownInfo}>
                    <p className={styles.dropdownTitle}>
                      {getItemTitle(item, searchTerm)}
                    </p>
                    {getItemHash(item) && (
                      <p className={styles.dropdownHash}>{getItemHash(item)}</p>
                    )}
                  </div>
                  {item.timestamp && (
                    <span className={styles.dropdownTime}>{formatTime(item.timestamp)}</span>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </form>
  );
};

export default SearchBarDesktop;
